<script setup lang="ts">
import { toRefs, ref, onMounted, computed } from 'vue';
import { formatterPrice, formatDateAndHourSpanish } from '../../helpers';
import { describeResponseMips } from '../../helpers/responseMips';
import { formatDateTime, getStatus } from '../../services/utils/Formatters';
import OperationsRepository from '@repositories/operations.repository';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPolicy } from '../../types/policy.type';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
const store = Store();
const { operations }: any = storeToRefs(store);

const operationsRepository = new OperationsRepository();

const emit = defineEmits<{
  (e: 'updateCurrentPolicyClient', value: IPolicy): void;
  (e: 'updateCurrentPolicy', value: IPolicy): void;
}>();

const props = defineProps({
  policyData: {
    type: Object as () => IPolicy,
    default: () => ({
      id: '',
      createdAt: '',
      manager: { email: '' },
      client: { tradeName: '', chubbPersonaId: '' },
      travelStartDate: '',
      travelEndDate: '',
      indemnificationLimit: 0,
      clientId: 0,
      sourceCityZipCode: { MunicipioNombre: '', code: '' },
      destinationCityZipCode: { MunicipioNombre: '', code: '' },
      stop1: undefined,
      stop2: undefined,
      stop3: undefined,
      companyGPS: { description: '' },
      documentDescription: '',
      asset: { description: '' },
      truckPlate: '',
      trailerPlate: '',
      driverName: '',
      driverCellphone: '',
      driverEmail: '',
      status: '',
      statusChubbnet: null,
    }),
  },
});

const statusInformation = ref<string | null>(null);
type PolicyAction =
  | 'createClientInChubb'
  | 'quote'
  | 'issue'
  | 'generatePdf'
  | 'downloadPdf'
  | 'solveManually'
  | 'sendPdf';
const runningAction = ref<PolicyAction | null>(null);
const backendErrorMessage = ref<string | null>(null);
const isStatusInformationError = computed(() => [
  t('global.ocurrio_un_error'),
  t('operaciones.modal_content_policy_mx.ocurrio_un_error_al_descargar_la_imagen'),
  t('operaciones.modal_content_policy_mx.ocurrio_un_error_intentelo_mas_tarde'),
  backendErrorMessage.value,
].includes(statusInformation.value ?? ''));
const dateLastEmailWithPolicy = ref<string | null>(null);
const { policyData } = toRefs(props);

onMounted(async () => {
	const response = await operationsRepository.getDateLastSendPolicyToClient(policyData.value.polizaId)
	dateLastEmailWithPolicy.value = response?.date ?? null
});

const handleCreateClientInChubb = async (id: number) => {
  let index = operations.value.findIndex(
    (value: any) => value.id === policyData.value.id
  );

  runningAction.value = 'createClientInChubb';
  statusInformation.value = t('operaciones.modal_content_policy_mx.cargando_cliente_como_persona_chubb');
  try {
    const { data } = await operationsRepository.createClientInChubb(id);

    emit('updateCurrentPolicyClient', data.data.client);
    store.setProp({
      key: 'operations',
      index,
      value: { ...operations.value[index], client: data.data.client },
    });

    runningAction.value = null;
    statusInformation.value = null;
  } catch (error: any) {
    backendErrorMessage.value = error?.message ?? null;
    statusInformation.value = error?.message ?? t('global.ocurrio_un_error');
    runningAction.value = null;
  }
};

const downloadDocument = async () => {
  const response = await operationsRepository.downloadClientDocument({
    clientId: policyData.value.clientId,
    policyId: policyData.value.id,
    documentPath: policyData.value.documentPath,
  });

  if (!response) {
    statusInformation.value = t('operaciones.modal_content_policy_mx.ocurrio_un_error_al_descargar_la_imagen');
  }
};

const handleQuotePolicy = async () => {
  let index = operations.value.findIndex(
    (value: any) => value.id === policyData.value.id
  );

  runningAction.value = 'quote';
  statusInformation.value = t('operaciones.modal_content_policy_mx.cotizando_poliza');
  try {
    const { data } = await operationsRepository.quotePolicy(
      policyData.value.id
    );
    emit('updateCurrentPolicy', data);
    store.setProp({
      key: 'operations',
      index,
      value: data,
    });
    runningAction.value = null;
    statusInformation.value = null;
  } catch (error: any) {
    if (error?.status !== 403) {
      policyData.value.statusChubbnet = 'ERROR_CHUBBNET';
    }
    backendErrorMessage.value = error?.message ?? null;
    statusInformation.value = error?.message ?? t('global.ocurrio_un_error');
    runningAction.value = null;
  }
};

const handleGetLinkPayment = async () => {
  try {
    statusInformation.value = t('operaciones.modal_content_policy_mx.generando_url');
    const { data } = await operationsRepository.getPaymentURL(
      policyData.value.id
    );
    window.open(data, '_blank');
    statusInformation.value = null;
  } catch (error) {
    statusInformation.value = t('global.ocurrio_un_error');
  }
};

const handleIssuePolicy = async () => {
  let index = operations.value.findIndex(
    (value: any) => value.id === policyData.value.id
  );

  runningAction.value = 'issue';
  statusInformation.value = t('operaciones.modal_content_policy_mx.emitiendo_poliza');
  try {
    const { data } = await operationsRepository.createIssuePolicy(
      policyData.value.id
    );

    emit('updateCurrentPolicy', data);
    store.setProp({
      key: 'operations',
      index,
      value: data,
    });
    runningAction.value = null;
    statusInformation.value = null;
  } catch (error: any) {
    if (error?.status !== 403) {
      policyData.value.statusChubbnet = 'ERROR_CHUBBNET';
    }
    backendErrorMessage.value = error?.message ?? null;
    statusInformation.value = error?.message ?? t('global.ocurrio_un_error');
    runningAction.value = null;
  }
};

const getStatusChubbnet = (status: IPolicy['statusChubbnet']) => {
  const labels = {
    IN_PROCESS: t('operaciones.modal_content_policy_mx.estado_chubbnet_en_proceso'),
    ERROR_CHUBBNET: t('operaciones.modal_content_policy_mx.estado_chubbnet_error'),
    PROCESS_FINISHED: t('operaciones.modal_content_policy_mx.estado_chubbnet_finalizado'),
  };

  return status ? labels[status] ?? '' : t('operaciones.modal_content_policy_mx.sin_informar');
};

const handleGetPDFPolicy = async () => {
  let index = operations.value.findIndex(
    (value: any) => value.id === policyData.value.id
  );

  runningAction.value = 'generatePdf';
  statusInformation.value = t('operaciones.modal_content_policy_mx.generando_pdf');

  try {
    await operationsRepository.getPDFPolicy(policyData.value.id);

    store.setProp({
      key: 'operations',
      index,
      value: {
        ...operations.value[index],
        pdfStatus: 1,
        status: 3,
        statusChubbnet: 'PROCESS_FINISHED',
      },
    });
    policyData.value.pdfStatus = 1;
    policyData.value.status = 3;
    policyData.value.statusChubbnet = 'PROCESS_FINISHED';
    runningAction.value = null;
    statusInformation.value = null;
  } catch (error: any) {
    if (error?.status !== 403) {
      policyData.value.statusChubbnet = 'ERROR_CHUBBNET';
    }
    backendErrorMessage.value = error?.message ?? null;
    statusInformation.value = error?.message ?? t('operaciones.modal_content_policy_mx.ocurrio_un_error_intentelo_mas_tarde');
    runningAction.value = null;
  }
};

const handleDownloadPDFPolicy = async () => {
  runningAction.value = 'downloadPdf';
  statusInformation.value = t('operaciones.modal_content_policy_mx.descargando_pdf');

  try {
    await operationsRepository.downloadPDFPolicy({
      clientId: policyData.value.clientId,
      policyId: policyData.value.id,
      policyNumberId: policyData.value.polizaId,
    });

    runningAction.value = null;
    statusInformation.value = null;
  } catch (error) {
    statusInformation.value = t('operaciones.modal_content_policy_mx.ocurrio_un_error_intentelo_mas_tarde');
    runningAction.value = null;
  }
};

const handleSolvedManually = async () => {
  let index = operations.value.findIndex(
    (value: any) => value.id === policyData.value.id
  );

  runningAction.value = 'solveManually';
  statusInformation.value = t('operaciones.modal_content_policy_mx.resolviendo_manualmente');
  try {
    const { data } = await operationsRepository.solvedManually(
      policyData.value.id
    );

    emit('updateCurrentPolicy', data);
    store.setProp({
      key: 'operations',
      index,
      value: data,
    });
    runningAction.value = null;
    statusInformation.value = null;
  } catch (error: any) {
    backendErrorMessage.value = error?.message ?? null;
    statusInformation.value = error?.message ?? t('global.ocurrio_un_error');
    runningAction.value = null;
  }
};

const handleSendChubbpdf = async () => {
  let index = operations.value.findIndex(
    (value: any) => value.id === policyData.value.id
  );
  runningAction.value = 'sendPdf';
  statusInformation.value = t('operaciones.modal_content_policy_mx.enviando_email_al_cliente');
  try {
    await operationsRepository.sendPDFtoClient(policyData.value.id);

    store.setProp({
      key: 'operations',
      index,
      value: {
        ...operations.value[index],
        status: 4,
        statusChubbnet: 'PROCESS_FINISHED',
      },
    });

    policyData.value.status = 4;
    policyData.value.statusChubbnet = 'PROCESS_FINISHED';

    runningAction.value = null;

    statusInformation.value = t('operaciones.modal_content_policy_mx.poliza_enviada');

    setTimeout(async() => {
      statusInformation.value = null;
    }, 2000);
    setTimeout(async() => {
	  const response = await operationsRepository.getDateLastSendPolicyToClient(policyData.value.polizaId)
	  dateLastEmailWithPolicy.value = response?.date ?? null
    }, 5000);
  } catch (error: any) {
    if (error?.status !== 403) {
      policyData.value.statusChubbnet = 'ERROR_CHUBBNET';
    }
    backendErrorMessage.value = error?.message ?? null;
    statusInformation.value = error?.message ?? t('global.ocurrio_un_error');
    runningAction.value = null;
  }
};
</script>

<template>
  <div>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.fecha_de_creacion') }}:</strong>
      {{ formatDateTime(policyData.createdAt.toString()) }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.creada_por') }}:</strong>
      {{ policyData.manager.email }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.cliente') }}:</strong>
      {{ policyData.client.tradeName }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.inicio_del_viaje') }}:</strong>
      {{ formatDateAndHourSpanish(policyData.travelStartDate) }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.fin_del_viaje') }}:</strong>
      {{ formatDateAndHourSpanish(policyData.travelEndDate) }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.suma_asegurada') }}:</strong>
      {{ formatterPrice(policyData.indemnificationLimit) }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.desde') }}:</strong>
      {{ policyData.sourceCityZipCode?.MunicipioNombre }}
      <strong>CP:</strong> {{ policyData.sourceCityZipCode?.code }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.hasta') }}:</strong>
      {{ policyData.destinationCityZipCode?.MunicipioNombre }}
      <strong>CP:</strong> {{ policyData.destinationCityZipCode?.code }}
    </p>
    <p v-if="policyData.stop1">
      <strong>{{ t('operaciones.modal_content_policy_mx.parada_1') }}:</strong>
      {{ policyData.stop1 }}
    </p>
    <p v-if="policyData.stop2">
      <strong>{{ t('operaciones.modal_content_policy_mx.parada_2') }}:</strong>
      {{ policyData.stop2 }}
    </p>
    <p v-if="policyData.stop3">
      <strong>{{ t('operaciones.modal_content_policy_mx.parada_3') }}:</strong>
      {{ policyData.stop3 }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.compania_gps') }}:</strong>
      {{ policyData.companyGPS.description }}
    </p>
    <div class="d-flex">
      <p class="m-0 mx-2">
        <strong>{{ t('operaciones.modal_content_policy_mx.documento') }}:</strong>
        {{ policyData.documentDescription }}
      </p>
      <CButton
        v-if="policyData.documentPath"
        class="background-blue button__form m-0"
        @click="downloadDocument"
      >
        <template #content>{{ t('operaciones.modal_content_policy_mx.descargar_documento') }}</template>
      </CButton>
    </div>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.tipo_de_mercancia') }}: </strong>
      {{ policyData.asset.description }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.placa_camion') }}: </strong>
      {{ policyData.truckPlate }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.placa_caja') }}: </strong>
      {{ policyData.trailerPlate }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.chofer') }}: </strong>
      {{ policyData.driverName }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.telefono_chofer') }}: </strong>
      {{ policyData.driverCellphone }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.email_chofer') }}: </strong>
      {{ policyData.driverEmail }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.respuesta_mips') }}: </strong>
	  {{ describeResponseMips(policyData?.responseMips) }}
    </p>
    <p v-if="policyData.versionId">
      <strong>{{ t('operaciones.modal_content_policy_mx.chubb_cotizacion') }}: </strong>
      {{ policyData.cotizacionId }}
      <strong>{{ t('operaciones.modal_content_policy_mx.version_id') }}: </strong>
      {{ policyData.versionId }}
    </p>
    <p>
      <strong>{{ t('operaciones.modal_content_policy_mx.estado_chubbnet') }}: </strong>
      {{ getStatusChubbnet(policyData.statusChubbnet) }}
    </p>
    <div class="d-flex mb-2">
      <p class="m-0 mx-2">
        <strong>{{ t('operaciones.modal_content_policy_mx.estado_cotizacion') }}: </strong>
        {{ getStatus(policyData.status) }}
      </p>
      <CButton
        v-if="policyData.packageStatus === 1"
        class="background-blue button__form m-0"
        @click="handleGetLinkPayment"
      >
        <template #content>{{ t('operaciones.modal_content_policy_mx.link_de_pago') }}</template>
      </CButton>
    </div>
    <p v-if="policyData.polizaId">
      <strong>{{ t('operaciones.modal_content_policy_mx.chubb_poliza') }}: </strong>
      {{ policyData.polizaId }}
    </p>
    <p v-if="policyData.claveId">
      <strong>{{ t('operaciones.modal_content_policy_mx.clave_id') }}: </strong>
      {{ policyData.claveId }}
    </p>
    <p v-if="policyData.numeroConsecutivo">
      <strong>{{ t('operaciones.modal_content_policy_mx.numero_consecutivo') }}: </strong>
      {{ policyData.numeroConsecutivo }}
    </p>
	<p v-if="dateLastEmailWithPolicy">
      <strong>{{ t('operaciones.modal_content_policy_mx.ultimo_envio_de_poliza') }}: </strong>
      {{ formatDateTime(dateLastEmailWithPolicy) }}
    </p>
  </div>

  <template
    v-if="policyData.status !== 5 && policyData.statusChubbnet !== 'IN_PROCESS'"
  >
    <p v-if="statusInformation" :class="{ 'text-danger': isStatusInformationError }">{{ statusInformation }}</p>
    <CButton
      class="background-blue button__form w-100"
      style="padding: 0.55rem !important"
      v-if="!policyData.client.chubbPersonaId"
      @click="handleCreateClientInChubb(policyData.clientId)"
      :loading="runningAction === 'createClientInChubb'"
      :disabled="runningAction !== null"
    >
      <template #content v-if="runningAction !== 'createClientInChubb'">{{ t('operaciones.modal_content_policy_mx.cargar_cliente_chubb') }}</template>
    </CButton>
    <CButton
      class="background-blue button__form w-100"
      style="padding: 0.55rem !important"
      v-if="policyData.status === 0"
      @click="handleQuotePolicy()"
      :loading="runningAction === 'quote'"
      :disabled="runningAction !== null"
    >
      <template #content v-if="runningAction !== 'quote'">{{ t('operaciones.modal_content_policy_mx.cotizar_poliza') }}</template>
    </CButton>
    <CButton
      class="background-blue button__form w-100"
      style="padding: 0.55rem !important"
      v-if="policyData.status === 1"
      @click="handleIssuePolicy"
      :loading="runningAction === 'issue'"
      :disabled="runningAction !== null"
    >
      <template #content v-if="runningAction !== 'issue'">{{ t('operaciones.modal_content_policy_mx.emitir_poliza') }}</template>
    </CButton>
    <template v-if="policyData.polizaId">
      <CButton
        class="background-blue button__form w-100"
        style="padding: 0.55rem !important"
        v-if="policyData.pdfStatus == 0 || (policyData.polizaId && !policyData.pdfStatus)"
        @click="handleGetPDFPolicy"
        :loading="runningAction === 'generatePdf'"
        :disabled="runningAction !== null"
      >
        <template #content v-if="runningAction !== 'generatePdf'">{{ t('operaciones.modal_content_policy_mx.generar_pdf_poliza') }}</template>
      </CButton>
      <CButton
        class="background-blue button__form w-100"
        style="padding: 0.55rem !important"
        v-if="policyData.pdfStatus == 1"
        @click="handleDownloadPDFPolicy"
        :disabled="runningAction !== null"
      >
        <template #content>{{ t('operaciones.modal_content_policy_mx.descargar_pdf_poliza') }}</template>
      </CButton>
      <CButton
        class="background-blue button__form w-100"
        style="padding: 0.55rem !important"
        v-if="policyData.pdfStatus == 1"
        @click="handleSendChubbpdf"
        :disabled="runningAction !== null"
		>
        <template #content>{{ t('operaciones.modal_content_policy_mx.enviar_pdf_poliza_a_cliente') }}</template>
      </CButton>
    </template>

    <CButton
      class="background-blue button__form w-100"
      style="padding: 0.55rem !important"
      @click="handleSolvedManually"
      :disabled="runningAction !== null"
      v-if="policyData.status !== 4"
    >
      <template #content>{{ t('operaciones.modal_content_policy_mx.marcar_como_resuelta_manualmente') }}</template>
    </CButton>
  </template>
</template>

<style scoped>
p {
  margin: 10px;
}
</style>
